/**
 * GS1 element-string parser for GS1-128 / GS1 DataMatrix payloads —
 * the healthcare/logistics format (GTIN + expiry + batch + serial).
 * Pure function over the decoded text; nothing leaves the device.
 */

export interface GS1Element {
  ai: string;
  label: string;
  value: string;
}

const GS = "\u001d";

interface AiSpec {
  label: string;
  /** Fixed data length, or null for variable (GS- or end-terminated). */
  length: number | null;
  kind?: "date" | "decimal";
  maxLength?: number;
}

/** Common AIs (exact match). */
const AI_TABLE: Record<string, AiSpec> = {
  "00": { label: "SSCC", length: 18 },
  "01": { label: "GTIN", length: 14 },
  "02": { label: "Contained GTIN", length: 14 },
  "10": { label: "Batch/Lot", length: null, maxLength: 20 },
  "11": { label: "Production Date", length: 6, kind: "date" },
  "13": { label: "Packaging Date", length: 6, kind: "date" },
  "15": { label: "Best Before Date", length: 6, kind: "date" },
  "17": { label: "Expiration Date", length: 6, kind: "date" },
  "20": { label: "Product Variant", length: 2 },
  "21": { label: "Serial Number", length: null, maxLength: 20 },
  "240": { label: "Additional ID", length: null, maxLength: 30 },
  "241": { label: "Customer Part Number", length: null, maxLength: 30 },
  "30": { label: "Variable Count", length: null, maxLength: 8 },
  "37": { label: "Count", length: null, maxLength: 8 },
  "400": { label: "Order Number", length: null, maxLength: 30 },
  "8005": { label: "Price Per Unit", length: 6 },
  "8008": { label: "Production Date/Time", length: null, maxLength: 12 },
};

/** Decimal-weight family: 310d..316d = net weight kg etc., d = decimal places. */
const DECIMAL_PREFIXES: Record<string, string> = {
  "310": "Net Weight (kg)",
  "311": "Length (m)",
  "312": "Width (m)",
  "313": "Height (m)",
  "314": "Area (m²)",
  "315": "Net Volume (l)",
  "316": "Net Volume (m³)",
};

function formatDate(v: string): string | null {
  if (!/^\d{6}$/.test(v)) return null;
  const yy = Number(v.slice(0, 2));
  const year = yy <= 50 ? 2000 + yy : 1900 + yy;
  const month = v.slice(2, 4);
  const day = v.slice(4, 6);
  if (Number(month) < 1 || Number(month) > 12) return null;
  return day === "00" ? `${year}-${month}` : `${year}-${month}-${day}`;
}

export function parseGS1(data: string): GS1Element[] | null {
  if (!data) return null;
  // Strip AIM symbology identifiers (]C1 = GS1-128, ]d2 = GS1 DataMatrix, …)
  let rest = data.replace(/^\][A-Za-z]\d/, "");
  const elements: GS1Element[] = [];

  while (rest.length > 0) {
    if (rest.startsWith(GS)) {
      rest = rest.slice(1);
      continue;
    }

    let spec: AiSpec | undefined;
    let ai = "";

    // Decimal families first (4-digit AI, last digit = decimal position).
    const four = rest.slice(0, 4);
    if (/^\d{4}$/.test(four) && DECIMAL_PREFIXES[four.slice(0, 3)]) {
      ai = four;
      spec = { label: DECIMAL_PREFIXES[four.slice(0, 3)], length: 6, kind: "decimal" };
    } else {
      for (const len of [4, 3, 2]) {
        const candidate = rest.slice(0, len);
        if (AI_TABLE[candidate]) {
          ai = candidate;
          spec = AI_TABLE[candidate];
          break;
        }
      }
      // Company-internal AIs 91–99.
      if (!spec && /^9[1-9]/.test(rest)) {
        ai = rest.slice(0, 2);
        spec = { label: `Internal (${ai})`, length: null, maxLength: 90 };
      }
    }

    if (!spec) return elements.length > 0 ? elements : null;
    rest = rest.slice(ai.length);

    let raw: string;
    if (spec.length !== null) {
      if (rest.length < spec.length) return elements.length > 0 ? elements : null;
      raw = rest.slice(0, spec.length);
      rest = rest.slice(spec.length);
    } else {
      const gsIndex = rest.indexOf(GS);
      raw = gsIndex === -1 ? rest : rest.slice(0, gsIndex);
      rest = gsIndex === -1 ? "" : rest.slice(gsIndex + 1);
      if (raw.length === 0) return elements.length > 0 ? elements : null;
    }

    let value = raw;
    if (spec.kind === "date") {
      const formatted = formatDate(raw);
      if (!formatted) return elements.length > 0 ? elements : null;
      value = formatted;
    } else if (spec.kind === "decimal") {
      if (!/^\d{6}$/.test(raw)) return elements.length > 0 ? elements : null;
      const decimals = Number(ai[3]);
      value = String(Number(raw) / 10 ** decimals);
    }

    elements.push({ ai, label: spec.label, value });
  }

  return elements.length > 0 ? elements : null;
}
